import React, { useEffect, useState, useCallback } from 'react'

import {
  CTable,
  CTableHead,
  CTableHeaderCell,
  CTableBody,
  CTableRow,
  CTableDataCell,
  CBadge,
  CSpinner,
} from '@coreui/react-pro'
import moment from 'moment'
import CIcon from '@coreui/icons-react'
import { cilHamburgerMenu } from '@coreui/icons'

import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd'

import AdsManagerService from 'src/service/AdsManagerService'
import ToastComponent from 'src/components/common/TaostComponent'

// Mirrors AdsManagerController::UNORDERED_RANK - where cards with no saved order sit.
const UNORDERED_RANK = 999999

const DisplayOrder = ({ refreshDisplayOrder }) => {
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(false)

  // =========================================
  // FETCH DATA
  // =========================================
  const fetchDisplayOrder = useCallback(async () => {
    try {
      setLoading(true)

      const params = new URLSearchParams()
      params.append('limit', 500)
      const res = await AdsManagerService.gameAdsListing(0, 500, 1, params)

      if (res?.data?.data) {
        // A card this game has never reordered comes back with a null order_number. Fall
        // back to the same rank the API sorts those with (UNORDERED_RANK) so this list and
        // the table above agree - `?? 0` would float them to the top instead.
        const sorted = [...res.data.data].sort(
          (a, b) => (a.order_number ?? UNORDERED_RANK) - (b.order_number ?? UNORDERED_RANK),
        )

        setItems(sorted)
      }
    } catch (error) {
      console.log(error)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchDisplayOrder()
  }, [fetchDisplayOrder, refreshDisplayOrder])

  // =========================================
  // DRAG END
  // =========================================
  const handleDragEnd = async (result) => {
    if (!result.destination) return

    const reordered = Array.from(items)

    const [removed] = reordered.splice(result.source.index, 1)

    reordered.splice(result.destination.index, 0, removed)

    // Update local order_number
    const updatedItems = reordered.map((item, index) => ({
      ...item,
      order_number: index + 1,
    }))

    setItems(updatedItems)

    try {
      // API PAYLOAD
      const payload = updatedItems.map((item) => ({
        id: item.id,
        order_number: item.order_number,
      }))

      // API CALL
      await AdsManagerService.updateDisplayOrder(payload)

      ToastComponent('Display order updated successfully', 'success')
    } catch (error) {
      console.log(error)

      ToastComponent('Failed to update order', 'error')

      fetchDisplayOrder()
    }
  }

  return (
    <>
      {loading ? (
        <div className="text-center p-3">
          <CSpinner />
        </div>
      ) : (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="table-body">
            {(provided) => (
              <div ref={provided.innerRef} {...provided.droppableProps}>
                <CTable hover responsive bordered>
                  <CTableHead color="light">
                    <CTableRow>
                      <CTableHeaderCell style={{ width: '60px' }}>Drag</CTableHeaderCell>

                      <CTableHeaderCell>Order</CTableHeaderCell>

                      <CTableHeaderCell>Promo Title</CTableHeaderCell>

                      <CTableHeaderCell>Promo Description</CTableHeaderCell>
                      <CTableHeaderCell>Promo Validity</CTableHeaderCell>
                      <CTableHeaderCell>Promo Start Date</CTableHeaderCell>
                    </CTableRow>
                  </CTableHead>

                  <CTableBody>
                    {items.map((item, index) => (
                      <Draggable
                        key={item.id.toString()}
                        draggableId={item.id.toString()}
                        index={index}
                      >
                        {(provided, snapshot) => (
                          <CTableRow
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            style={{
                              background: snapshot.isDragging ? '#f3f2ff' : '#fff',
                              ...provided.draggableProps.style,
                            }}
                          >
                            {/* DRAG ICON */}
                            <CTableDataCell
                              {...provided.dragHandleProps}
                              style={{
                                cursor: 'grab',
                                width: '60px',
                                textAlign: 'center',
                              }}
                            >
                              <CIcon icon={cilHamburgerMenu} size="lg" />
                            </CTableDataCell>

                            {/* ORDER */}
                            <CTableDataCell>
                              <CBadge color="primary">{item.order_number ?? index + 1}</CBadge>
                            </CTableDataCell>

                            {/* TITLE */}
                            <CTableDataCell>{item.title}</CTableDataCell>

                            {/* DESCRIPTION */}
                            <CTableDataCell>{item.description || '-'}</CTableDataCell>
                            <CTableDataCell>
                              {moment(item.validity_date).format('D.MM.YYYY')}
                            </CTableDataCell>
                            <CTableDataCell>
                              {moment(item.start_date).format('D.MM.YYYY')}
                            </CTableDataCell>
                          </CTableRow>
                        )}
                      </Draggable>
                    ))}

                    {provided.placeholder}
                  </CTableBody>
                </CTable>
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}
    </>
  )
}

export default DisplayOrder
